/**
 * Hausaufgaben aus Mitschrift/Lernzettel übernehmen. Der Slash-Befehl
 * „/hausaufgabe" schreibt einen Container in den Text:
 *
 *   ::: hausaufgabe
 *   Frist: 31.07.2026
 *   Thema: Aufgabe 3a–c, **Tabelle** und alles andere an Markdown
 *   :::
 *
 * Gerendert wird daraus eine Box (siehe `md-homework` in src/markdown); beim
 * Speichern wandert jeder Block in die Hausaufgaben.
 *
 * Die Verknüpfung Block <-> Datensatz steht **ausschließlich in der Datenbank**
 * (`homework.sourceNote` = ID der Notiz), nie im Text – im Quelltext soll nur
 * stehen, was der Nutzer geschrieben hat. Innerhalb einer Notiz werden Blöcke
 * und Datensätze zuerst über gleichen Text gepaart, der Rest der Reihe nach.
 * Damit gilt:
 *
 *   Block bearbeitet   -> derselbe Datensatz wird geändert, nicht neu angelegt.
 *   Block gelöscht     -> der Datensatz wird gelöscht.
 *   Von Hand angelegte Hausaufgaben (`sourceNote` leer) bleiben unberührt.
 */

import { topicsService, homeworkService, notesService, timetableService, holidaysService, findHoliday } from "@/services";
import { addDays, toLocalISODate, upcomingSubjectSlots } from "@/features/timetable/schedule";
import type { Homework } from "@/types/models";

const BLOCK_RE = /^[ \t]*:::+[ \t]*hausaufgabe[ \t]*$([\s\S]*?)^[ \t]*:::+[ \t]*$/gim;
/**
 * Eine frühere Fassung schrieb die Verknüpfung als `ID:`-Zeile in den Text.
 * Solche Zeilen werden beim nächsten Speichern entfernt.
 */
const LEGACY_ID_LINE = /^[ \t]*ID[ \t]*:[ \t]*[A-Za-z0-9_-]+[ \t]*$/i;

export function homeworkSnippet(dueDate: Date = new Date()): string {
  const formatted = dueDate.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  // `¦` markiert die Cursorposition nach dem Einfügen (siehe insertSnippet).
  return `\n::: hausaufgabe\nFrist: ${formatted}\nThema: ¦\n:::\n`;
}

/**
 * Datum der nächsten Unterrichtsstunde des Fachs (Ferien übersprungen) –
 * Vorbelegung für `homeworkSnippet()`. Eine heute schon begonnene Stunde zählt
 * nicht, auch nicht die gerade laufende: die Frist ist die NÄCHSTE Stunde.
 * Fällt auf heute zurück, wenn das Fach keine Stunde im Stundenplan hat oder
 * in den nächsten 12 Wochen keine ferienfreie Stunde findet.
 */
export async function nextLessonDate(subjectId: string): Promise<Date> {
  const today = new Date();
  const [settings, slots, holidays] = await Promise.all([
    timetableService.getSettings(),
    timetableService.listSlots(),
    holidaysService.forRange(today, addDays(today, 90)),
  ]);
  const candidates = upcomingSubjectSlots({
    slots,
    periods: settings.periods,
    days: settings.days,
    subjectId,
    from: today,
    weeksAhead: 12,
    skipStarted: true,
  });
  const next = candidates.find((s) => !findHoliday(holidays, toLocalISODate(s.date)));
  return next?.date ?? today;
}

/** „31.07.2026" oder „2026-07-31" -> ISO; leer bei unbrauchbarer Eingabe. */
export function parseDueDate(value: string): string {
  const raw = value.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (iso) return raw;
  const de = /^(\d{1,2})\.(\d{1,2})\.(\d{2,4})$/.exec(raw);
  if (!de) return "";
  const year = Number(de[3]) < 100 ? 2000 + Number(de[3]) : Number(de[3]);
  const d = new Date(year, Number(de[2]) - 1, Number(de[1]));
  return isNaN(d.getTime()) ? "" : toLocalISODate(d);
}

export interface ParsedHomework {
  dueDate: string;
  text: string;
}

/** Zerlegt alle `::: hausaufgabe`-Blöcke in Frist und Aufgabentext. */
export function parseHomeworkBlocks(content: string): ParsedHomework[] {
  const out: ParsedHomework[] = [];
  for (const [, body] of content.matchAll(BLOCK_RE)) {
    let dueDate = "";
    const lines: string[] = [];
    for (const line of body.split("\n")) {
      const due = /^[ \t]*Frist[ \t]*:[ \t]*(.*)$/i.exec(line);
      if (due && !dueDate) {
        dueDate = parseDueDate(due[1]);
        continue;
      }
      if (LEGACY_ID_LINE.test(line)) continue;
      lines.push(line.replace(/^[ \t]*(?:Thema|Aufgabe)[ \t]*:[ \t]*/i, ""));
    }
    const text = lines.join("\n").trim();
    if (dueDate && text) out.push({ dueDate, text });
  }
  return out;
}

/** Entfernt Alt-Verknüpfungen (`ID:`-Zeilen) aus den Hausaufgaben-Blöcken. */
export function stripLegacyLinks(content: string): string {
  if (!/^[ \t]*ID[ \t]*:/im.test(content)) return content;
  return content.replace(BLOCK_RE, (block) =>
    block
      .split("\n")
      .filter((line) => !LEGACY_ID_LINE.test(line))
      .join("\n")
  );
}

/**
 * Entfernt den zu `text` gehörenden `::: hausaufgabe`-Block aus einer Notiz –
 * Gegenstück zum Anlegen in `syncHomeworkFromNote`. Ohne das käme eine über
 * die Hausaufgaben-Seite gelöschte Aufgabe beim nächsten Speichern der Notiz
 * automatisch zurück: der Block stünde weiterhin unverknüpft im Text und
 * `syncHomeworkFromNote` legt für jeden unverknüpften Block einen neuen
 * Datensatz an. Bei mehreren Blöcken mit identischem Text wird nur der erste
 * entfernt (Paarung ist ohnehin nur über Text möglich, siehe oben).
 */
async function removeHomeworkBlockFromNote(noteId: string, text: string): Promise<void> {
  const note = await notesService.get(noteId).catch(() => null);
  if (!note) return;
  const cleaned = stripLegacyLinks(note.content);
  const target = text.trim();
  let removed = false;
  const next = cleaned.replace(BLOCK_RE, (block) => {
    if (removed) return block;
    if (parseHomeworkBlocks(block)[0]?.text !== target) return block;
    removed = true;
    return "";
  });
  if (removed) await notesService.save(noteId, next);
}

/**
 * Einzige Löschstelle für eine Hausaufgabe: entfernt den Datensatz UND –
 * falls er aus einem `::: hausaufgabe`-Block stammt (`sourceNote` gesetzt) –
 * gleich den passenden Block aus der Notiz. Jede Löschung in der UI muss
 * hierüber laufen, nicht direkt über `homeworkService.remove`.
 */
export async function deleteHomework(hw: Homework): Promise<void> {
  await homeworkService.remove(hw.id);
  if (hw.sourceNote) await removeHomeworkBlockFromNote(hw.sourceNote, hw.text).catch(() => {});
}

export interface HomeworkSync {
  created: number;
  updated: number;
  removed: number;
  /**
   * Der Notiztext, bereinigt um Alt-Verknüpfungen. Weicht er vom Eingabetext
   * ab, sollte der Aufrufer ihn speichern.
   */
  content: string;
}

/**
 * Gleicht alle `::: hausaufgabe`-Blöcke einer Notiz mit den Hausaufgaben ab.
 * `noteId` ist die Notiz, zu der die Blöcke gehören – nur Datensätze mit genau
 * dieser `sourceNote` werden angefasst.
 */
export async function syncHomeworkFromNote(
  content: string,
  topicId: string,
  noteId: string
): Promise<HomeworkSync> {
  const cleaned = stripLegacyLinks(content);
  const blocks = parseHomeworkBlocks(cleaned);

  const [topic, all] = await Promise.all([
    topicsService.get(topicId),
    homeworkService.listAll(),
  ]);
  // Anlagereihenfolge = Reihenfolge, in der die Blöcke einst entstanden sind.
  const mine = all
    .filter((hw) => hw.sourceNote === noteId)
    .sort((a, b) => a.created.localeCompare(b.created));

  if (!blocks.length && !mine.length) {
    return { created: 0, updated: 0, removed: 0, content: cleaned };
  }

  // Paarung: erst textgleich (überlebt das Verschieben von Blöcken), danach
  // der Reihe nach (fängt das Bearbeiten eines Textes ab).
  const free = [...mine];
  const pairs = new Map<number, Homework>();
  blocks.forEach((b, i) => {
    const k = free.findIndex((hw) => hw.text.trim() === b.text);
    if (k >= 0) pairs.set(i, free.splice(k, 1)[0]);
  });
  blocks.forEach((_, i) => {
    if (pairs.has(i) || !free.length) return;
    pairs.set(i, free.shift() as Homework);
  });

  let created = 0;
  let updated = 0;
  let removed = 0;
  /** Schon übernommene Alt-Datensätze (ohne `sourceNote`). */
  const adopted = new Set<string>();

  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i];
    const hw = pairs.get(i);
    if (hw) {
      if (hw.text.trim() !== b.text || hw.dueDate !== b.dueDate) {
        await homeworkService.update(hw.id, { text: b.text, dueDate: b.dueDate });
        updated++;
      }
      continue;
    }
    // Aus der Zeit vor `sourceNote`: gleicher Text, gleiches Fach, noch ohne
    // Verknüpfung -> übernehmen statt ein Duplikat anzulegen.
    const orphan = all.find(
      (o) =>
        !o.sourceNote &&
        !adopted.has(o.id) &&
        o.subject === topic.subject &&
        o.text.trim().toLowerCase() === b.text.toLowerCase()
    );
    if (orphan) {
      adopted.add(orphan.id);
      await homeworkService.update(orphan.id, { sourceNote: noteId, dueDate: b.dueDate });
      continue;
    }
    await homeworkService.create({
      subject: topic.subject,
      topic: topicId,
      text: b.text,
      dueDate: b.dueDate,
      sourceNote: noteId,
    });
    created++;
  }

  // Übrig gebliebene Datensätze: ihr Block steht nicht mehr im Text.
  for (const hw of free) {
    await homeworkService.remove(hw.id);
    removed++;
  }

  return { created, updated, removed, content: cleaned };
}
